import React, { useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Typography,
  Tooltip,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import {
  useGetTableData2,
  useDeleteTableDataPreset,
  useCreateData,
} from "../Plan/tableHooks";
import { groupSchedules, isValidDateFormat } from "./utils";
import * as S from "./styles";
import Loading from "../Loading/Loading";
import ScheduleBox from "./MachineScheduleBox";
import { useMachineViewerData } from "../MchMaster/useMachineViewerData";
import { useParams } from "react-router-dom";
import goToHelpLink from "../../../utils/goToHelpLink";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

function MachineSchedule({ openSnackbar }) {
  const { group_code, project_code } = useParams();
  const { equipment } = useMachineViewerData(group_code, project_code);
  const { presets } = useGetTableData2(
    true,
    `/api/factory/getTypeM?group_code=${group_code}&project_code=${project_code}`,
    "presets"
  );
  const { operMchTimeData } = useGetTableData2(
    true,
    `/api/factory/getMchM?group_code=${group_code}&project_code=${project_code}`,
    "operMchTimeData"
  );
  const [expandedId, setExpandedId] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [selectedDates, setSelectedDates] = useState({
    startDate: "",
    endDate: "",
  });
  const { mutate: deleteMutate, isPending: isDeleteLoading } =
    useDeleteTableDataPreset(true, "/api/factory/deleteOperMchTime", true);
  const { mutate: createMutate, isPending: isCreateLoading } = useCreateData(
    true,
    "/api/factory/createMchTime",
    openSnackbar
  );
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };
  const handleDelete = (machineId, schedule) => {
    const deleteData = `${machineId},${schedule.presetId},${schedule.startDate},${schedule.endDate}`;
    if (window.confirm("삭제하시겠습니까? 해당 작업은 취소할 수 없습니다.")) {
      deleteMutate(
        {
          id: deleteData,
          project_code,
          group_code,
        },
        {
          onSuccess: () => {
            openSnackbar("성공적으로 삭제하였습니다.");
          },
          onError: (error) => {
            openSnackbar(error.response.data.message, "error");
            console.error(error.response.data.message);
          },
        }
      );
    }
  };
  const handleSubmit = async (e, machineId) => {
    e.preventDefault();
    if (
      selectedPreset === undefined ||
      selectedPreset === null ||
      !selectedDates.startDate ||
      !selectedDates.endDate
    ) {
      openSnackbar("모든 필드를 입력해주세요.", "info");
      return;
    }
    try {
      const dates = [];
      const currentDate = new Date(selectedDates.startDate);
      const lastDate = new Date(selectedDates.endDate);
      if (currentDate > lastDate) {
        openSnackbar("시작일이 종료일보다 늦을 수 없습니다.", "info");
        return;
      }
      if (
        !isValidDateFormat(selectedDates.startDate) ||
        !isValidDateFormat(selectedDates.endDate)
      ) {
        openSnackbar("날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)", "info");
        return;
      }
      // 오늘 날짜 체크 제거
      // const today = new Date();
      // today.setHours(0, 0, 0, 0);
      while (currentDate <= lastDate) {
        // if (currentDate >= today) {
        //   dates.push(new Date(currentDate).toISOString().split("T")[0]);
        // }
        dates.push(new Date(currentDate).toISOString().split("T")[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      const scheduleData = dates.map((date) => ({
        machine_id: machineId,
        oper_date: date,
        type_id: selectedPreset,
        group_code,
        project_code,
      }));
      createMutate(scheduleData, {
        onSuccess: () => {
          setSelectedPreset("");
          setSelectedDates({ startDate: "", endDate: "" });
          setExpandedId(null);
          openSnackbar("장비 일정을 추가했습니다.");
        },
        onError: (error, variables, context) => {
          openSnackbar(error.response.data.message, "error");
        },
      });
    } catch (error) {
      console.log(error.response.data.message);
    }
  };
  const getScheduleSummary = (machineId) => {
    const machineSchedules = operMchTimeData.filter(
      (schedule) => schedule.machine_id === machineId
    );
    if (!machineSchedules?.length) return null;

    const groups = groupSchedules(machineSchedules, presets)
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      .map((group) => {
        const presetName = group.type_id === -1 ? "휴일" : group.presetName;
        return {
          startDate: group.startDate,
          endDate: group.endDate,
          presetName,
          presetId: group.type_id,
          displayText:
            group.startDate === group.endDate
              ? `${group.startDate} : ${presetName}`
              : `${group.startDate} ~ ${group.endDate} : ${presetName}`,
        };
      });

    if (!groups.length) return null;
    return groups;
  };
  return (
    <>
      <S.ContainerMchSch maxWidth={false}>
        {/* <S.TitleMchSch variant="h4">장비 스케줄러</S.TitleMchSch> */}
        <S.HeaderPreset>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "22px !important",
                color: "#111",
              }}
            >
              설비별 가동시간 등록&nbsp;
            </Typography>
            <Tooltip
              title="이동"
              sx={{ cursor: "pointer" }}
              onClick={() =>
                goToHelpLink(
                  "https://help.ese-tech.com/ko/scheduler#h-9-3-%EC%9E%A5%EB%B9%84-%EC%9D%BC%EC%A0%95-%EA%B4%80%EB%A6%AC"
                )
              }
            >
              <HelpOutlineIcon />
            </Tooltip>
          </div>
        </S.HeaderPreset>
        <S.GridContainerMchSch>
          {equipment.map((item) => (
            <S.StyledCardMchSch key={item.machine_id}>
              <S.CardHeaderMchSch onClick={() => toggleExpand(item.machine_id)}>
                <S.CardTitleMchSch variant="h6">
                  {item.machine_name || item.name}
                </S.CardTitleMchSch>
                {expandedId === item.machine_id ? <RemoveIcon /> : <AddIcon />}
              </S.CardHeaderMchSch>
              {expandedId !== item.machine_id && (
                <>
                  {getScheduleSummary(item.machine_id) ? (
                    <S.ScheduleSummaryContainerMchSch>
                      {getScheduleSummary(item.machine_id).map(
                        (schedule, index) => (
                          <ScheduleBox
                            key={index}
                            machineId={item.machine_id}
                            schedule={schedule}
                            handleDelete={handleDelete}
                          />
                        )
                      )}
                    </S.ScheduleSummaryContainerMchSch>
                  ) : (
                    <S.NoScheduleMessageMchSch>
                      등록된 스케줄 없음
                    </S.NoScheduleMessageMchSch>
                  )}
                </>
              )}
              <S.ExpandableContentMchSch
                $expanded={expandedId === item.machine_id}
              >
                {getScheduleSummary(item.machine_id) ? (
                  <S.ScheduleSummaryContainerMchSch>
                    {getScheduleSummary(item.machine_id).map(
                      (schedule, index) => (
                        <ScheduleBox
                          key={index}
                          machineId={item.machine_id}
                          schedule={schedule}
                          handleDelete={handleDelete}
                        />
                      )
                    )}
                  </S.ScheduleSummaryContainerMchSch>
                ) : (
                  <S.NoScheduleMessageMchSch>
                    등록된 스케줄 없음
                  </S.NoScheduleMessageMchSch>
                )}
                <S.ScheduleFormMchSch
                  onSubmit={(e) => handleSubmit(e, item.machine_id)}
                >
                  <S.FormRowMchSch>
                    <TextField
                      type="date"
                      size="small"
                      value={selectedDates.startDate}
                      onChange={(e) =>
                        setSelectedDates((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="시작일"
                    />
                    <TextField
                      type="date"
                      size="small"
                      value={selectedDates.endDate}
                      onChange={(e) =>
                        setSelectedDates((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="종료일"
                    />
                  </S.FormRowMchSch>
                  <S.FormRowMchSch>
                    <S.StyledSelectMchSch
                      size="small"
                      value={selectedPreset}
                      onChange={(e) => setSelectedPreset(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>유형 선택</em>
                      </MenuItem>
                      <MenuItem value={-1}>휴일</MenuItem>
                      {presets?.map((preset) => (
                        <MenuItem key={preset.id} value={preset.id}>
                          {preset.name}
                        </MenuItem>
                      ))}
                    </S.StyledSelectMchSch>
                    <Button variant="contained" size="small" type="submit">
                      적용
                    </Button>
                  </S.FormRowMchSch>
                </S.ScheduleFormMchSch>
              </S.ExpandableContentMchSch>
            </S.StyledCardMchSch>
          ))}
        </S.GridContainerMchSch>
      </S.ContainerMchSch>
      {(isDeleteLoading || isCreateLoading) && <Loading />}
    </>
  );
}

export default MachineSchedule;
